import { create } from 'zustand';
import { getCameras, getCameraStream } from '@/api/cameras';
import type { CameraResponse } from '@/types/camera';

interface CameraState {
  cameras: CameraResponse[];
  selectedCamera: string | null;
  splitView: 1 | 4 | 9;
  isLayoutEditMode: boolean;
  isLoading: boolean;
  streamType: 'live' | 'ai';
  streamUrls: Record<string, string>;
  streamExpiry: Record<string, string | null>;
  generation: number;
  requestId: number;
  reset: () => void;

  fetchCameras: (siteId?: string, zoneId?: string) => Promise<void>;
  setSelectedCamera: (id: string | null) => void;
  setSplitView: (view: 1 | 4 | 9) => void;
  setIsLayoutEditMode: (mode: boolean) => void;
  setStreamType: (type: 'live' | 'ai') => void;
  fetchStreamUrl: (cameraId: string, streamType?: 'live' | 'ai') => Promise<string | null>;
  invalidateStreamCache: (cameraId: string, streamType: 'live' | 'ai') => void;
}

export const useCameraStore = create<CameraState>((set, get) => ({
  cameras: [],
  selectedCamera: null,
  splitView: 4,
  isLayoutEditMode: false,
  isLoading: false,
  streamType: 'live',
  streamUrls: {},
  streamExpiry: {},
  generation: 0,
  requestId: 0,
  reset: () => set((s) => ({ cameras: [], selectedCamera: null, isLayoutEditMode: false,
    streamUrls: {}, streamExpiry: {}, isLoading: false, generation: s.generation + 1,
    requestId: s.requestId + 1 })),

  fetchCameras: async (siteId, zoneId) => {
    const generation = get().generation;
    const requestId = get().requestId + 1;
    set({ isLoading: true, requestId });
    try {
      const data = await getCameras(siteId, zoneId);
      if (get().generation !== generation || get().requestId !== requestId) return;
      set({ cameras: data.items, isLoading: false });
    } catch {
      if (get().generation !== generation || get().requestId !== requestId) return;
      set({ cameras: [], isLoading: false });
    }
  },

  setSelectedCamera: (id) => set({ selectedCamera: id }),
  setSplitView: (view) => set({ splitView: view }),
  setIsLayoutEditMode: (mode) => set({ isLayoutEditMode: mode }),
  setStreamType: (type) => set({ streamType: type }),

  fetchStreamUrl: async (cameraId, type) => {
    const generation = get().generation;
    const streamType = type ?? get().streamType;
    const cacheKey = `${cameraId}:${streamType}`;
    const cached = get().streamUrls[cacheKey];
    const expiry = get().streamExpiry[cacheKey];

    // 캐시 있고 만료 5분 이상 남았으면 재사용
    if (cached && (!expiry || new Date(expiry).getTime() - Date.now() > 5 * 60 * 1000)) {
      return cached;
    }

    try {
      const data = await getCameraStream(cameraId, streamType);
      if (get().generation !== generation) return null;
      if (data.stream_url) {
        set((state) => ({
          streamUrls: { ...state.streamUrls, [cacheKey]: data.stream_url },
          streamExpiry: { ...state.streamExpiry, [cacheKey]: data.expires_at },
        }));
        return data.stream_url;
      }
      return null;
    } catch {
      return null;
    }
  },

  invalidateStreamCache: (cameraId, streamType) => {
    const cacheKey = `${cameraId}:${streamType}`;
    set((state) => {
      const { [cacheKey]: _url, ...restUrls } = state.streamUrls;
      const { [cacheKey]: _exp, ...restExpiry } = state.streamExpiry;
      return { streamUrls: restUrls, streamExpiry: restExpiry };
    });
  },
}));
